import {
  ColorsV2,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Spacer,
  Text,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useLockDeviceCompat } from '@/hooks/useLockDeviceCompat';
import { DeviceAsset } from '@/types/assets';

type LockDeviceModalProps = {
  organizationId: string;
  deviceAsset: DeviceAsset;
  employeeName: string;
  onClose: () => void;
};

const LockDeviceModal = ({
  organizationId,
  deviceAsset,
  employeeName,
  onClose,
}: LockDeviceModalProps) => {
  const { t } = useTranslation('people');
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [lockDevice, { isLoading }] = useLockDeviceCompat();

  const deviceId = deviceAsset.device?.id;
  const serialNumber = deviceAsset.serialNumber;

  const handleSubmit = async () => {
    try {
      await lockDevice({
        organizationId,
        deviceId,
        serialNumber,
      }).unwrap();
      showSuccessToast({
        id: 'lock-device-success-toast',
        message: t('employeeProfile.modals.lockDevice.successMessage'),
      });
      onClose();
    } catch (error) {
      console.error('Error sending lock command: ', error);
      showErrorToast({
        id: 'lock-device-error-toast',
        message: t('employeeProfile.modals.lockDevice.errorMessage'),
      });
    }
  };

  return (
    <ModalV2 visible hide={onClose} ariaLabelledby="lock-device">
      <ModalHeader>
        <Text variant="heading-2">
          {employeeName
            ? t('employeeProfile.modals.lockDevice.title', { employeeName })
            : t('employeeProfile.modals.lockDevice.titleWithoutEmployee')}
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text>{t('employeeProfile.modals.lockDevice.confirmationLine1')}</Text>
        <Spacer />
        <Text>{t('employeeProfile.modals.lockDevice.confirmationLine2')}</Text>
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction id="lock-device-cancel" onClick={onClose} variant="text">
            {t('employeeProfile.modals.lockDevice.cancel')}
          </ModalAction>
          <ModalAction
            backgroundColor={ColorsV2.ERROR}
            id="lock-device-submit"
            onClick={handleSubmit}
            loading={isLoading}>
            {t('employeeProfile.modals.lockDevice.confirm')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default LockDeviceModal;
